import React, { useState, useEffect } from "react";
import {
  Link,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";

// --- Core Imports ---
import { useAuth } from "../../contexts/AuthContext";
import ProtectedRoute from "../../components/ProtectedRoute";
import {
  COMPENSATION_AUTH,
  COMPENSATION_MENU_ITEMS,
} from "../../config/authorizationConfig";

// --- Page Components ---
import Payroll from "./Payroll/Payroll";
import SalaryRegister from "./SalaryRegister";
import SalaryBudget from "./SalaryBudget";
import PayrollSummary from "./PayrollSummary";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import UpgradePlan from "../UpgradePlan";

// --- Styles ---
import "../../styles/InnerMenu.scss";

const CompensationBenefits = () => {
  const { isAuthorized, canAccessModuleFeature, user, isLoading } = useAuth();
  const [visibleSubmenu, setVisibleSubmenu] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Enhanced authorization check that properly validates feature access
  const hasFeatureAccess = (authConfig) => {
    // If no auth config, it's public access
    if (!authConfig) {
      return true;
    }

    // Check roles first
    if (
      authConfig.roles &&
      !authConfig.roles.some((role) => user?.authorities?.includes(role))
    ) {
      return false;
    }

    // If feature is specified, check feature access
    if (authConfig.feature) {
      return canAccessModuleFeature(authConfig.feature);
    }

    // If only roles specified and user has required role, allow access
    return true;
  };

  // Use the menu items imported from the central config file
  const menuItems = COMPENSATION_MENU_ITEMS;

  // Enhanced menu items with access information - show all items
  const enhancedMenuItems = menuItems.map((item) => {
    if (item.subItems) {
      // For items with subitems, check each subitem access
      return {
        ...item,
        hasAccess: true, // Always show parent menu
        subItemsAccess: item.routes.map(() => hasFeatureAccess(item.auth)),
      };
    } else {
      // For direct items, check their access
      return {
        ...item,
        hasAccess: hasFeatureAccess(item.auth),
      };
    }
  });

  // Helper function to check if user can access a specific submenu item
  const canAccessSubItem = (menuItem, subIndex) => {
    if (!menuItem.subItemsAccess) return hasFeatureAccess(menuItem.auth);
    return menuItem.subItemsAccess[subIndex];
  };

  useEffect(() => {
    const activeIndex = enhancedMenuItems.findIndex((item) =>
      item.routes.includes(location.pathname)
    );
    if (activeIndex !== -1) {
      setVisibleSubmenu(activeIndex);
    }
  }, [location.pathname, enhancedMenuItems]);

  const handleMenuClick = (index) => {
    const item = enhancedMenuItems[index];
    const isAlreadyOpen = visibleSubmenu === index;

    if (item.subItems && item.subItems.length > 0) {
      setVisibleSubmenu(isAlreadyOpen ? null : index);

      // Find first accessible sub-route
      let firstAccessibleRoute = null;
      for (let routeIndex = 0; routeIndex < item.routes.length; routeIndex++) {
        if (canAccessSubItem(item, routeIndex)) {
          firstAccessibleRoute = item.routes[routeIndex];
          break;
        }
      }

      // Navigate to accessible route or first route (will show upgrade page)
      if (!isAlreadyOpen) {
        navigate(firstAccessibleRoute || item.routes[0]);
      }
    } else {
      setVisibleSubmenu(null);
      navigate(item.routes[0]);
    }
  };

  const getDefaultRedirect = () => {
    // Find first accessible route
    for (const item of enhancedMenuItems) {
      if (item.subItems) {
        // Check subitems for accessible routes
        for (let i = 0; i < item.routes.length; i++) {
          if (canAccessSubItem(item, i)) {
            return item.routes[i];
          }
        }
      } else if (item.hasAccess) {
        return item.routes[0];
      }
    }

    // If no accessible routes, redirect to first route (will show upgrade)
    if (enhancedMenuItems.length > 0) {
      return enhancedMenuItems[0].routes[0];
    }

    // Fallback if no items
    return "/dashboard";
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="custom-ess-container">
      <div className="custom-menu">
        {enhancedMenuItems.map((item, index) => {
          const isActive =
            visibleSubmenu === index || item.routes.includes(location.pathname);
          const hasAccess = item.hasAccess;

          return (
            <div
              key={item.name}
              className={`custom-menu-item ${isActive ? "custom-active" : ""} ${
                !hasAccess ? "custom-locked" : ""
              }`}
              onClick={() => handleMenuClick(index)}
              style={{
                opacity: hasAccess ? 1 : 0.6,
                position: "relative",
              }}
            >
              {item.subItems ? (
                <>
                  {item.name}
                  {!hasAccess && (
                    <Icon
                      icon="mdi:lock"
                      style={{
                        marginLeft: "8px",
                        fontSize: "14px",
                        color: "#f39c12",
                      }}
                    />
                  )}
                  <span className="arrow-icon">
                    <Icon
                      icon="mdi:chevron-down"
                      style={{
                        transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  </span>
                </>
              ) : (
                <Link to={item.routes[0]} className="custom-menu-link">
                  {item.name}
                  {!hasAccess && (
                    <Icon
                      icon="mdi:lock"
                      style={{
                        marginLeft: "8px",
                        fontSize: "14px",
                        color: "#f39c12",
                      }}
                    />
                  )}
                </Link>
              )}
            </div>
          );
        })}
      </div>

      {visibleSubmenu !== null &&
        enhancedMenuItems[visibleSubmenu]?.subItems?.length > 0 && (
          <div className="custom-submenu-container">
            <div className="custom-analytics-submenu">
              {enhancedMenuItems[visibleSubmenu].subItems.map(
                (subItem, subIndex) => {
                  const route =
                    enhancedMenuItems[visibleSubmenu].routes[subIndex];
                  const hasAccess = canAccessSubItem(
                    enhancedMenuItems[visibleSubmenu],
                    subIndex
                  );

                  return (
                    <Link
                      key={subItem}
                      to={route}
                      className={`custom-submenu-item ${
                        location.pathname === route ? "custom-active" : ""
                      } ${!hasAccess ? "custom-locked" : ""}`}
                      style={{
                        opacity: hasAccess ? 1 : 0.6,
                        position: "relative",
                      }}
                    >
                      {subItem}
                      {!hasAccess && (
                        <Icon
                          icon="mdi:lock"
                          style={{
                            marginLeft: "8px",
                            fontSize: "14px",
                            color: "#f39c12",
                          }}
                        />
                      )}
                    </Link>
                  );
                }
              )}
            </div>
          </div>
        )}

      <div className="custom-content">
        <Routes>
          {/* Enhanced routes with upgrade plan fallback */}
          <Route
            path="/payroll"
            element={
              hasFeatureAccess(COMPENSATION_AUTH.PAYROLL) ? (
                <Payroll />
              ) : (
                <UpgradePlan
                  featureName="Payroll Management"
                  description="Process and manage company-wide payroll operations"
                />
              )
            }
          />
          <Route
            path="/payroll-report/salary-register"
            element={
              hasFeatureAccess(COMPENSATION_AUTH.PAYROLL_REPORT) ? (
                <SalaryRegister />
              ) : (
                <UpgradePlan
                  featureName="Salary Register Report"
                  description="Generate detailed salary register reports for employees"
                />
              )
            }
          />
          <Route
            path="/payroll-report/salary-budget"
            element={
              hasFeatureAccess(COMPENSATION_AUTH.PAYROLL_REPORT) ? (
                <SalaryBudget />
              ) : (
                <UpgradePlan
                  featureName="Salary Budget Report"
                  description="View and analyze salary budget allocations and spending"
                />
              )
            }
          />
          <Route
            path="/payroll-report/summary"
            element={
              hasFeatureAccess(COMPENSATION_AUTH.PAYROLL_REPORT) ? (
                <PayrollSummary />
              ) : (
                <UpgradePlan
                  featureName="Payroll Summary Report"
                  description="Access comprehensive payroll summary and analytics"
                />
              )
            }
          />
          <Route
            path="/"
            element={<Navigate to={getDefaultRedirect()} replace />}
          />
        </Routes>
      </div>
    </div>
  );
};

export default CompensationBenefits;
